/**
 * PROD-1107 — mark which students' names are written family-name-first.
 *
 * Ordo has no student-detail screen, so this is the surface: search the
 * campus roster, flip the students whose names read family-name-first
 * ("Nguyen Van An"). It is a fact about the STUDENT's name, so it is set
 * per student rather than as a school-wide preference — one roster holds
 * both kinds of name.
 *
 * Reuses the cafeteria directory search rather than adding a second student
 * list: it already returns the composed name, the flag and the grade, and
 * SCHOOL_ADMIN already passes its gate. The row shows the name exactly as
 * Cafeteria will render it, so the toggle is its own preview.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Switch } from '@ordo/ui/switch'
import { UserPen } from 'lucide-react'
import { type ReactNode, useEffect, useId, useState } from 'react'

import { useCafeteriaStudentsQuery, useSetStudentNameOrderMutation } from '@/api/cafeteria'
import type { AdminSchool } from '@/api/settings-grade-ranges'
import { todayLocalIso } from '@/lib/cafeteria/format'

import { CardTitleRow } from './campus-scope'

const SEARCH_DEBOUNCE_MS = 300

export interface StudentNameOrderCardProps {
  school: AdminSchool
  /**
   * The section's campus picker (`CampusScope`), rendered in this card's
   * header. Absent for a single-campus org — then the header renders exactly
   * as it did before per-card scoping.
   */
  scopeControl?: ReactNode
}

export function StudentNameOrderCard({ school, scopeControl }: StudentNameOrderCardProps) {
  const inputId = useId()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchText.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchText])

  // Today's roster: the directory is scoped to the term covering the service
  // date, and "who is enrolled right now" is what an admin is editing.
  const serviceDate = todayLocalIso()
  const studentsQuery = useCafeteriaStudentsQuery({
    schoolLocationId: school.id,
    serviceDate,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  })
  const setNameOrder = useSetStudentNameOrderMutation()
  const students = studentsQuery.data?.students ?? []

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitleRow scopeControl={scopeControl}>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPen className="h-4 w-4" />
            Student name order
          </CardTitle>
        </CardTitleRow>
        <p className="text-sm text-muted-foreground">
          Most names are written given name first. Turn this on for students whose name is written family name first —
          Cafeteria will then show, print and export it that way.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={inputId}>Find a student</Label>
          <Input
            id={inputId}
            type="search"
            autoComplete="off"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by name or student ID"
          />
        </div>

        {studentsQuery.isPending ? (
          <p className="text-sm text-muted-foreground">Loading students…</p>
        ) : students.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {debouncedSearch ? 'No students match that search.' : 'No students are enrolled at this school today.'}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border">
            {students.map((student) => (
              <li key={student.studentUserId} className="flex items-center justify-between gap-4 px-3 py-2">
                <div className="min-w-0">
                  {/* Rendered by the same formatter Cafeteria uses, so the row
                      is a live preview of the change. */}
                  <p className="truncate text-sm font-medium">{student.studentName}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[
                      student.gradeLevel ? `Grade ${student.gradeLevel}` : null,
                      student.rosterName,
                      student.ordoStudentId,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="hidden text-xs text-muted-foreground sm:inline">Family name first</span>
                  <Switch
                    checked={student.studentFamilyNameFirst}
                    disabled={setNameOrder.isPending}
                    onCheckedChange={(checked) => {
                      setNameOrder.mutate({
                        studentUserId: student.studentUserId,
                        schoolLocationId: school.id,
                        serviceDate,
                        familyNameFirst: checked,
                      })
                    }}
                    aria-label={`Write ${student.studentName}'s name family name first`}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
